import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { TALK_SESSION_LISTING_TYPE } from '../../util/sessionPricing';
import { isListingOnline } from '../../util/presence';
import { openBookATime } from '../../util/bookingIntent';
import {
  LISTENER_STATUS,
  formatNextAvailableClock,
  listenerStatusMessageId,
  resolveListenerAvailability,
} from '../../util/listenerAvailability';
import usePresenceMap from '../../util/usePresenceMap';

import css from './AvailableNowBadge.module.css';

const statusFromOnline = online => (online ? LISTENER_STATUS.AVAILABLE : LISTENER_STATUS.OFFLINE);

/**
 * Status pip on a Listening profile photo or avatar.
 * Green = Available Now, yellow = In a Conversation. Hidden when Offline.
 *
 * @param {Object} props
 * @param {boolean} [props.online]
 * @param {string} [props.status]
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
export const AvailableNowDot = props => {
  const { online, status, className } = props;
  const resolved = status || statusFromOnline(online);
  if (resolved === LISTENER_STATUS.OFFLINE) {
    return null;
  }
  return (
    <span
      className={classNames(
        css.photoDot,
        (resolved === LISTENER_STATUS.IN_CONVERSATION || resolved === LISTENER_STATUS.BUSY) ? css.photoDotBusy : css.photoDotAvailable,
        className
      )}
      data-available-now-dot={(resolved === LISTENER_STATUS.IN_CONVERSATION || resolved === LISTENER_STATUS.BUSY) ? 'busy' : 'online'}
      aria-hidden="true"
    />
  );
};

/**
 * Always-visible availability chip with green / yellow / gray dots.
 */
const AvailableNowBadge = props => {
  const { online, status, nextAvailableAt, timeZone, className, rootClassName, onClick } = props;
  const resolved = status || statusFromOnline(online);
  const clockLabel = formatNextAvailableClock(nextAvailableAt, timeZone);
  const messageId = listenerStatusMessageId(resolved, clockLabel);
  const classes = classNames(rootClassName || css.root, className, {
    [css.available]: resolved === LISTENER_STATUS.AVAILABLE,
    [css.busy]: (resolved === LISTENER_STATUS.IN_CONVERSATION || resolved === LISTENER_STATUS.BUSY),
    [css.offline]: resolved === LISTENER_STATUS.OFFLINE,
  });
  const label = (
    <>
      <span className={css.dot} aria-hidden="true" />
      <FormattedMessage id={messageId} values={{ time: clockLabel }} />
    </>
  );

  if (typeof onClick === 'function') {
    return (
      <button type="button" className={classes} onClick={onClick} data-book-chip="true">
        {label}
      </button>
    );
  }

  return (
    <span className={classes} data-availability-status={resolved}>
      {label}
    </span>
  );
};

/**
 * Live Available-now flag for a talk-session listing. Persisted listing
 * flag plus optional heartbeat.
 *
 * @param {object|null} listing
 * @returns {boolean}
 */
export const useListingOnline = listing => {
  const listingId = listing?.id?.uuid;
  const listingType = listing?.attributes?.publicData?.listingType;
  const { map } = usePresenceMap(listingId ? [listingId] : []);
  if (listingType !== TALK_SESSION_LISTING_TYPE) {
    return false;
  }
  return isListingOnline(listingId, map, listing);
};

/**
 * Live status for a talk-session listing from presence extras.
 *
 * @param {object|null} listing
 * @returns {{ online: boolean, status: string, nextAvailableAt: number|null, startNowPaused: boolean, sessionBusy: boolean }}
 */
export const useListingAvailability = listing => {
  const listingId = listing?.id?.uuid;
  const listingType = listing?.attributes?.publicData?.listingType;
  const { map, pausedMap = {}, busyMap = {}, nextAvailableMap = {} } = usePresenceMap(
    listingId ? [listingId] : []
  );
  const online = listingType === TALK_SESSION_LISTING_TYPE && isListingOnline(listingId, map, listing);
  const startNowPaused = !!pausedMap[listingId];
  const sessionBusy = !!busyMap[listingId];
  const nextAvailableAt = nextAvailableMap[listingId] || null;
  const resolved = resolveListenerAvailability({
    listing,
    online,
    sessionBusy,
    startNowPaused,
    nextAvailableAt,
  });
  return {
    online,
    startNowPaused,
    sessionBusy,
    nextAvailableAt: resolved.nextAvailableAt,
    status: resolved.status,
    talkNow: resolved.talkNow,
    primaryCta: resolved.primaryCta,
    secondaryCta: resolved.secondaryCta,
  };
};

/**
 * Live badge for a listing page / author card. Reads the persisted flag.
 * `onlineOnly` hides the offline chip (ListingPage heading used ONLINE NOW).
 */
export const ListingAvailabilityBadge = props => {
  const { listing, className, rootClassName, opensBookATime = false, onlineOnly = false } = props;
  const history = useHistory();
  const location = useLocation();
  const listingType = listing?.attributes?.publicData?.listingType;
  const availability = useListingAvailability(listing);
  if (listingType !== TALK_SESSION_LISTING_TYPE) {
    return null;
  }
  if (onlineOnly && availability.status === LISTENER_STATUS.OFFLINE) {
    return null;
  }
  const onClick =
    opensBookATime && availability.status !== LISTENER_STATUS.AVAILABLE
      ? () => openBookATime(history, location)
      : undefined;
  return (
    <AvailableNowBadge
      status={availability.status}
      nextAvailableAt={availability.nextAvailableAt}
      timeZone={listing?.attributes?.availabilityPlan?.timezone}
      className={className}
      rootClassName={rootClassName}
      onClick={onClick}
    />
  );
};

export default AvailableNowBadge;
